import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AlbumFormValues,
  GalleryAlbum,
  GalleryPhoto,
} from '@/components/ui/admin/gallery';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { firstErrorMessage } from './term-versions';

export const galleryKeys = {
  all: ['gallery'] as const,
  albums: () => [...galleryKeys.all, 'albums'] as const,
  album: (id: string) => [...galleryKeys.all, 'albums', id] as const,
  photos: (albumId: string, order: 'asc' | 'desc') =>
    [...galleryKeys.all, 'photos', albumId, order] as const,
};

const albums = apiClient.albums as any;
const photos = apiClient.photos as any;

/** Página de fotos do álbum (a grade carrega mais ao rolar). */
export const PHOTOS_PER_PAGE = 24;

/** Fotos por requisição no envio — o limite do corpo da requisição cabe folgado em 10 fotos. */
const UPLOAD_BATCH = 10;

type Page<T> = {
  data: T[];
  pagination: { page: number; perPage: number; total: number; totalPages: number };
};

/** Chama a API e troca o erro do eden pela mensagem da regra ("Álbum inválido!"…). */
async function call<T>(request: Promise<{ data: T }>) {
  try {
    return (await request).data;
  } catch (e: any) {
    throw firstErrorMessage(e?.value);
  }
}

export type GalleryStorage = { total: number; used: number; storage: number };

/** Uso do disco do servidor (bytes) — o servidor recalcula a cada 5 minutos. */
export function useGalleryStorage() {
  return useQuery({
    queryKey: [...galleryKeys.all, 'storage'] as const,
    queryFn: () => call<GalleryStorage>(photos.storage.get()),
    staleTime: 5 * 60 * 1000,
  });
}

/** Álbuns com `photosCount` e `previewUrls` (mosaico do card). */
export function useGalleryAlbums() {
  return useQuery({
    queryKey: galleryKeys.albums(),
    queryFn: () =>
      call<Page<GalleryAlbum>>(
        albums.get({ query: { perPage: 200, orderBy: 'createdAt', order: 'desc' } }),
      ).then((res) => res.data),
  });
}

export function useGalleryAlbum(id: string) {
  return useQuery({
    queryKey: galleryKeys.album(id),
    queryFn: () => call<GalleryAlbum>(albums({ id }).get()),
    // Álbum inexistente não volta a existir: a tela sai logo pra lista.
    retry: false,
  });
}

export function useAlbumPhotos(albumId: string, order: 'asc' | 'desc') {
  return useInfiniteQuery({
    queryKey: galleryKeys.photos(albumId, order),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      call<Page<GalleryPhoto>>(
        photos.get({
          query: {
            albumId,
            page: pageParam,
            perPage: PHOTOS_PER_PAGE,
            // `id` é UUIDv7 (cronológico e único): `createdAt` empata dentro de um mesmo envio.
            orderBy: 'id',
            order,
          },
        }),
      ),
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.totalPages
        ? last.pagination.page + 1
        : undefined,
  });
}

function useGalleryMutation<TVars, TData>(
  mutationFn: (vars: TVars) => Promise<TData>,
  fallback: string,
) {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn,
    onError: (e: any) => toast.error(e?.message ?? fallback),
    onSettled: () => queryClient.invalidateQueries({ queryKey: galleryKeys.all }),
  });
}

/** Cria (sem `id`) ou edita um álbum. */
export const useSaveAlbum = () =>
  useGalleryMutation(
    ({ id, ...values }: AlbumFormValues & { id?: string }) =>
      call<GalleryAlbum>(id ? albums({ id }).patch(values) : albums.post(values)),
    'Erro ao salvar álbum',
  );

/** Exclui álbuns — as fotos de dentro saem junto (ver `AlbumService.remove`). */
export const useDeleteAlbums = () =>
  useGalleryMutation(
    (ids: string[]) => Promise.all(ids.map((id) => call(albums({ id }).delete()))),
    'Erro ao excluir álbuns',
  );

export const useSetAlbumCover = () =>
  useGalleryMutation(
    ({ id, url }: { id: string; url: string }) =>
      call<GalleryAlbum>(albums({ id }).patch({ coverImage: url })),
    'Erro ao definir capa',
  );

/** Exclui fotos em lote; se a capa sair, o backend passa a capa pra mais recente que sobrou. */
export const useDeletePhotos = () =>
  useGalleryMutation(
    (ids: string[]) =>
      call<{ success: boolean; message: string }>(photos.delete({ ids })),
    'Erro ao excluir fotos',
  );

/**
 * Envia as fotos pro álbum em lotes (`POST /photos/upload`, multipart). A primeira foto vira capa
 * se o álbum ainda não tiver uma. Devolve as fotos criadas, na ordem do envio.
 */
export async function uploadAlbumPhotos(albumId: string, files: File[]) {
  const created: GalleryPhoto[] = [];

  for (let i = 0; i < files.length; i += UPLOAD_BATCH) {
    const form = new FormData();
    form.append('albumId', albumId);
    for (const file of files.slice(i, i + UPLOAD_BATCH)) form.append('photos', file);

    const res = await fetch('/api/v1/photos/upload', { method: 'POST', body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw firstErrorMessage(body);

    created.push(...(body.photos as GalleryPhoto[]));
  }

  return created;
}

/** Todas as fotos já carregadas do álbum (páginas do infinite query achatadas). */
export const flattenPhotos = (data?: InfiniteData<Page<GalleryPhoto>>) =>
  data?.pages.flatMap((p) => p.data) ?? [];
